import {axios} from '../axios.config';
import {STATUS_OK, STATUS_NO_CONTENT, STATUS_NOT_FOUND} from "../../utils/constants";

export class api{

    /**
     * Method called when user wants to validate the captcha.
     * Hits the appropriate backend entry points on sps2 info backend
     *
     * @param cId               captcha identifier to be sent to the sps2 info backend
     * @param cText             captcha text entered by the user
     * @param validationHandler Handler used to set the validation status and turn off any displays that need to stop
     * @param resetCaptcha      Handler used to reset the captcha image
     * @param callBackFunction  Function to be called after the captcha has been validated
     * @returns {Promise<void>}
     */
    async validateCaptcha(cId, cText, validationHandler, resetCaptcha, callBackFunction){
        try {
            const response = await axios.get(`/captcha/validate/${cId}/${cText}`)
            if (response.data === true) {
                validationHandler(null, true, callBackFunction)
            } else {
                resetCaptcha(true)
            }
        } catch (e) {
            console.error(e)
        }
    }

    /**
     * Method called when user wants to obtain a captcha image.
     * Hits the appropriate backend entry points on sps2 info backend
     *
     * @param idHandler         Handler used to store the captcha identifier
     * @param imageHandler      Handler used to store the captcha image
     * @returns {Promise<void>}
     */
    async getCaptchaIdentifier(idHandler, imageHandler){
        try {
            const response = await axios.get(`/captcha/captchaID`)
            let captchaData = response.data.replace(/(")/g, "")
            await this.getCaptchaImage(imageHandler, captchaData)
            await idHandler(captchaData)
        } catch (e) {
            console.error(e)
        }
    }

    /**
     * Method called when user wants to obtain a captcha image.
     * Hits the appropriate backend entry points on sps2 info backend
     *
     * @param imageHandler      Handler used to store the captcha image
     * @param cId               Captcha identifier
     * @returns {Promise<void>}
     */
    async getCaptchaImage(imageHandler, cId) {
        try {
            const response = await axios.get(`/captcha/captchaImage/${cId}`)
            let data = response.data;
            const byteChar = atob(data)
            const byteNumbers = new Array(byteChar.length);
            for (let i = 0; i < byteChar.length; i++) {
                byteNumbers[i] = byteChar.charCodeAt(i);
            }
            const byteArray = await new Uint8Array(byteNumbers);
            const blob = await new Blob([byteArray], {type: "text/html"})
            await imageHandler(blob)
        } catch (e) {
            console.error(e)
        }
    }

    /**
     * Iterates through the controller reference tree in the Vin Decode object storing all the controllers in a list
     *
     * @param arr Controller Reference Tree
     * @returns {Promise<[]>} An array of controllers
     */
    async getControllers(arr){
        let availableControllerList = []
        for (let i = 0; i < arr.length; i++){
            availableControllerList.push(arr[i][0].controllerCode+" - "+arr[i][0].controllerLabel)
        }

        return availableControllerList
    }

    /**
     * Goes through the array of controllers and reference tree to store the functions to be displayed, in a list
     * @param arr The controller reference tree
     * @param controllers List of controllers
     * @returns {Promise<*>} A list functions
     */
    async getFunctions(arr, controllers){
        return controllers.map((key, index) =>
            arr[index][1])
    }

    /**
     * Goes through the array of functions to extract and store the methods to be displayed, in a list
     * @param arr Controller Reference Tree
     * @param functions List of functions
     * @returns {Promise<[]>} A list of methods
     */
    async getMethods(arr, functions){
        let availableMethodList = []
        for(let i = 0; i < functions.length; i++){
            let programmingMethods = []
            for (let j = 0; j < functions[i].length; j++){
                programmingMethods.push(functions[i][j][1])
            }
            availableMethodList.push(programmingMethods)
        }
        return availableMethodList
    }

    /**
     * Main function called to  call the methods to separate the controller reference tree into lists
     *
     * @param arr   Controller Reference Tree
     * @param stateHandler  Handler used to store the lists in the object that calls the api
     * @returns {Promise<void>}
     */
    async updateControllersFunctionsMethods(arr, stateHandler){
        let controllers = await this.getControllers(arr)
        let functions = await this.getFunctions(arr, controllers)
        let methods = await this.getMethods(arr, functions)
        await stateHandler(controllers, functions, methods)
    }

    /**
     * Method called when user wants to obtain cvn list for a part number. Hits the appropriate backend entry points
     * on sps2 info backend
     *
     * @param e the event
     * @param cvnHandler    Handler used to set cvn list and turn off any displays that need to stop rendering
     * @param cvnPartNumber Part number entered by the user to be sent to the sps2 info backend
     * @param errorHandler  Handler used for when an error code is returned from the sps2 info backend
     * @returns {Promise<void>}
     */
    async grabCvn(e, cvnHandler, cvnPartNumber, errorHandler){
        try {
            const response = await axios.get(`/cvn/partnumber/${cvnPartNumber}`)
            const { status, data } = response;
            if (status === STATUS_OK) {
                cvnHandler(data);
            } else {
                errorHandler(data, status)
            }
        } catch(e) {
            console.error(e)
            errorHandler();
        }
    }

    /**
     * Method called when needing to retrieve a Vin Decode object from the SPS2 Info backend
     *
     * @param e                 the event
     * @param vinId             vin number to be sent to SPS2 info backend
     * @param cfmHandler        handler used for updating the controller function method lists in the class that calls
     *                          the api
     * @param vinHandler        Handler used to set store the objects returned from the backend and turn on any displays
     *                          that need turning on
     * @param presenterHandler  Handler used to turn off any presenters that need to be turned off
     * @param errorHandler      Handler used when sps2 info backend returns an error code to the front end
     * @returns {Promise<void>}
     */
    async grabVinDecode(e, vinId, cfmHandler, vinHandler, presenterHandler, errorHandler){
        await presenterHandler()
        try {
            const response = await axios.get(`/vin/fixture/${vinId}`);
            const { status, data } = response;
            if (status === STATUS_OK) {
                const { controllerReferenceTree: { referenceTree }, spsvin: { type: adapter} } = data;
                await this.updateControllersFunctionsMethods(referenceTree, cfmHandler)
                await vinHandler(data, adapter)
            }
            else if(status === STATUS_NO_CONTENT) {//NO CONTENT
                errorHandler("No Supported Controllers Found", STATUS_NO_CONTENT)
            } else if (status === STATUS_NOT_FOUND){
                errorHandler("Unable to process VIN", STATUS_NOT_FOUND)
            }
            else{
                errorHandler(data, status)
            }
        } catch(e) {
            console.error(e)
            errorHandler()
        }
    }

    /**
     * Method that sets data that needs to be set in the vin decode object after user has made the appropriate
     * selections
     * @param vinDecode             VinDecode object
     * @param displayName           List of display name items to be set in VinDecode object
     * @param controllerFunction    List of controller function items to be set in VinDecode object
     * @param programmingMethod     List of programming method items to be set in VinDecode object
     * @returns {Promise<*>}
     */
    async setVinDecode(vinDecode, displayName, controllerFunction, programmingMethod){
        vinDecode.displayName.controllerCode = displayName[0]
        vinDecode.displayName.controllerLabel = displayName[1]
        vinDecode.displayName.displayOrder = displayName[2]

        vinDecode.controllerFunction.displayOrder = controllerFunction[0]
        vinDecode.controllerFunction.functionLabel = controllerFunction[1]

        vinDecode.programmingMethod.methodId = programmingMethod[0]
        vinDecode.programmingMethod.description = programmingMethod[1]
        vinDecode.programmingMethod.blockSameCal = programmingMethod[2]

        return vinDecode
    }

    /**
     * Method used to set the production option in the VinDecode object if necessary
     * @param vinDecode         VinDecode Object
     * @param productionOption  List of production option items
     * @param selections        Selections object to be inserted into VinDecode production option
     * @returns {Promise<*>}
     */
    async setVinDecodePostOp(vinDecode, productionOption, selections){
        vinDecode.productionOption.label = productionOption[0]
        vinDecode.productionOption.selectedCode = productionOption[1]
        vinDecode.productionOption.selections = selections

        return vinDecode
    }

    /**
     * Helper method to set headers for the axios calls
     */
    headersHelper() {
        return {
            "Accept": "*/*",
        };
    }

    /**
     * Method called when trying to retrieve Calibration Data from Sps2 Info Backend
     * @param vinDecode    VinDecode object
     * @param programmingSequenceHandler    Handler uses to store calibration data and turn on calibration presenter
     * @param errorHandler  Handler used when sps2 info backend returns an error code
     * @returns {Promise<void>}
     */
    async getCalibrationData(vinDecode, programmingSequenceHandler, errorHandler){
        try {
            const response = await axios.post(`/progseq`, vinDecode, {headers: this.headersHelper()});
            programmingSequenceHandler(response.data);
        } catch (e) {
            console.error(e);
            errorHandler();
        }
    }

    /**
     * Method called when Gspas vin was returned from SPS2info backend and the user had to do Gspas Question and Answer
     * @param gspasCalData A Gspas Calibration Data Object
     * @param gspasCalDataHandler   Handler used to set the Calibration data Object and turn on the calibration
     *                              presenter
     * @param errorHandler  Handler used when Sps2 Info backend returns an error
     * @returns {Promise<void>}
     */
    async getGspasQACalData(gspasCalData, gspasCalDataHandler, errorHandler){
        try {
            const response = await axios.post('/gspas/question/answer', gspasCalData, {headers: this.headersHelper()});
            gspasCalDataHandler(response.data);
        } catch (e) {
            console.error(e);
            errorHandler();
        }
    }

    /**
     * Method called when hardware selection is made for controllers within Gspas Vin, makes call to  SPS2 info
     * Backend to get updated calibrations
     * @param gspasCalData A Gspas Calibration Data Object
     * @param gspasCalDataHandler   Handler used to set the Calibration data Object and turn on the calibration
     *                              presenter
     * @param errorHandler  Handler used when Sps2 Info backend returns an error
     * @returns {Promise<void>}
     */
    async getGspasHardwareCalData(gspasCalData,gspasCalDataHandler, errorHandler){
        try {
            const response = await axios.post('/gspas/hardware', gspasCalData, {headers: this.headersHelper()});
            gspasCalDataHandler(response.data);
        } catch (e) {
            console.error(e);
            errorHandler();
        }
    }

    /**
     * Ends the users sessions by invalidating cookies when they exit the application
     *
     * @returns {Promise<void>}
     */
    async endUserSession(){
        try {
            await axios.get('/session/endSession')
        } catch(e) {
            console.error(e)
        }
    }

}


